import { useEffect, useMemo, useRef, useState } from "react";

type TimePickerProps = {
  id: string;
  value: string;
  stepSeconds?: number;
  describedBy?: string;
  invalid?: boolean;
  onChange: (value: string) => void;
};

const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0"));

function timeParts(value: string): { hour: string; minute: string } {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  return { hour: match?.[1] ?? "16", minute: match?.[2] ?? "00" };
}

function minuteOptions(stepSeconds = 600): string[] {
  const stepMinutes = Math.max(1, Math.floor(stepSeconds / 60));
  return Array.from(
    { length: Math.ceil(60 / stepMinutes) },
    (_, index) => String(index * stepMinutes).padStart(2, "0"),
  ).filter((minute) => Number(minute) < 60);
}

export function TimePicker({
  id,
  value,
  stepSeconds = 600,
  describedBy,
  invalid = false,
  onChange,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const minutes = useMemo(() => minuteOptions(stepSeconds), [stepSeconds]);
  const { hour, minute } = timeParts(value);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    popupRef.current?.querySelectorAll<HTMLElement>('[aria-selected="true"]')
      .forEach((option) => option.scrollIntoView?.({ block: "center" }));
  }, [open]);

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div
      className="time-picker"
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          close();
        }
        if (event.altKey && event.key === "ArrowDown") {
          event.preventDefault();
          setOpen(true);
        }
      }}
    >
      <div className="time-picker-input-shell">
        <input
          id={id}
          ref={inputRef}
          className="time-picker-input"
          type="text"
          inputMode="none"
          value={value}
          readOnly
          role="combobox"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={`${id}-picker`}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onClick={() => setOpen((current) => !current)}
        />
        <span className="time-picker-icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" focusable="false">
            <circle cx="8" cy="8" r="5.5" />
            <path d="M8 4.75V8l2.2 1.35" />
          </svg>
        </span>
      </div>
      {open && (
        <div
          id={`${id}-picker`}
          ref={popupRef}
          className="time-picker-popup"
          role="dialog"
          aria-label="選擇預計抵達時間"
        >
          <div className="time-picker-header">
            <span>選擇時間</span>
            <button className="time-picker-done" type="button" onClick={close}>
              完成
            </button>
          </div>
          <div className="time-picker-columns">
            <div>
              {/* <p className="time-picker-column-label">時</p> */}
              <div className="time-picker-column" role="listbox" aria-label="小時">
                {hours.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-label={`${option} 時`}
                    aria-selected={option === hour}
                    onClick={() => onChange(`${option}:${minute}`)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            <span className="time-picker-separator" aria-hidden="true">:</span>
            <div>
              {/* <p className="time-picker-column-label">分</p> */}
              <div className="time-picker-column" role="listbox" aria-label="分鐘">
                {minutes.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-label={`${option} 分`}
                    aria-selected={option === minute}
                    onClick={() => onChange(`${hour}:${option}`)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
